import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';

const MAX_FILE_BYTES = 128 * 1024;
const MAX_FILES = 50;
const PRINT_STYLE = `
  @page zpl-sheet { size: 101.6mm 152.4mm !important; margin: 0 !important; }
  @media print {
    html, body { width: 101.6mm !important; margin: 0 !important; padding: 0 !important; }
    .temporary-zpl-page {
      page: zpl-sheet; width: 101.6mm !important; height: 152.4mm !important;
      margin: 0 !important; padding: 0 !important; overflow: hidden;
      break-after: page; break-inside: avoid;
    }
    .temporary-zpl-page:last-child { break-after: auto; }
  }
`;

interface PreviewProps {
  file: File;
  onClose: () => void;
}

function ZplPreview({ file, onClose }: PreviewProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(false);
  const [pages, setPages] = useState<string[]>([]);
  const [loaded, setLoaded] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const reader = new FileReader();
    activeRef.current = true;
    reader.onload = () => {
      void (async () => {
        try {
          const { renderFedExZpl } = await import('@/pages/oms/orders/utils/renderFedExZpl');
          if (controller.signal.aborted) {
            return;
          }
          const images = await renderFedExZpl(String(reader.result), controller.signal);
          if (!controller.signal.aborted) {
            setPages(images);
          }
        } catch {
          if (!controller.signal.aborted) {
            setError('Unable to prepare this ZPL file. Unsupported or invalid labels cannot be printed.');
          }
        }
      })();
    };
    reader.onerror = () => setError('Unable to read this file. Select it again.');
    reader.readAsText(file);
    return () => {
      activeRef.current = false;
      controller.abort();
      reader.abort();
    };
  }, [file]);

  const canPrint = !error && pages.length > 0 && loaded.size === pages.length;
  const print = useReactToPrint({
    contentRef,
    pageStyle: PRINT_STYLE,
    documentTitle: file.name,
    onBeforePrint: async () => {
      if (!canPrint || !activeRef.current) {
        throw new Error('The complete label set is not ready');
      }
      await document.fonts?.ready;
      if (!activeRef.current) {
        throw new Error('Printing cancelled');
      }
    },
    onPrintError: () => setError('Unable to open the print dialog. Close this preview and try again.'),
  });

  return (
    <Modal show onHide={onClose} size="lg" aria-labelledby="temporary-zpl-title">
      <Modal.Header closeButton>
        <Modal.Title id="temporary-zpl-title">{file.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Use 4 × 6 inch paper, actual size (100%), no margins, headers, or footers. Check the output and scan the
          barcodes before shipping.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {!error && pages.length === 0 && (
          <div role="status">
            <Spinner size="sm" /> Preparing label…
          </div>
        )}
        {!error && (
          <div style={{ overflowX: 'auto' }}>
            <div ref={contentRef}>
              {pages.map((source, index) => (
                <div key={index} className="temporary-zpl-page" style={{ width: '101.6mm', height: '152.4mm' }}>
                  <img
                    src={source}
                    alt={`Label page ${index + 1}`}
                    style={{ width: '100%', height: '100%', display: 'block', imageRendering: 'pixelated' }}
                    onLoad={() => setLoaded((previous) => new Set(previous).add(index))}
                    onError={() =>
                      setError('A label page could not be loaded. Printing the incomplete file is disabled.')
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={!canPrint}
          onClick={() => {
            if (canPrint) {
              print();
            }
          }}
        >
          Print this file
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Temporary local-only printing. Files are neither uploaded nor retained after leaving the page. */
export default function ZplPrintPage() {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [selected, setSelected] = useState<File | null>(null);
  return (
    <Container fluid className="py-3">
      <h2>Temporary ZPL Print</h2>
      <p className="text-muted">
        Select multiple FedEx-compatible 4 × 6 inch ZPL files, then preview and print one file at a time. Files stay in
        this browser only; no OMS or shipment changes are made.
      </p>
      <Form.Group controlId="temporary-zpl-files" className="mb-3">
        <Form.Label>ZPL files</Form.Label>
        <Form.Control
          type="file"
          accept=".zpl"
          multiple
          onChange={(event) => {
            const input = event.target as HTMLInputElement;
            const chosen = Array.from(input.files ?? []);
            input.value = '';
            if (chosen.length > MAX_FILES) {
              setErrors([`Select at most ${MAX_FILES} files at a time.`]);
              return;
            }
            const valid: File[] = [];
            const invalid: string[] = [];
            for (const file of chosen) {
              if (!file.name.toLowerCase().endsWith('.zpl')) {
                invalid.push(`${file.name}: Select a .zpl file.`);
              } else if (file.size === 0 || file.size > MAX_FILE_BYTES) {
                invalid.push(`${file.name}: Maximum file size is 128 KiB; empty files are not supported.`);
              } else {
                valid.push(file);
              }
            }
            setFiles(valid);
            setErrors(invalid);
          }}
        />
        <Form.Text>Up to 50 files, 128 KiB per file. A new selection replaces the list.</Form.Text>
      </Form.Group>
      {errors.length > 0 && (
        <Alert variant="danger">
          <ul className="mb-0">
            {errors.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}
      {files.length > 0 && (
        <>
          <Button
            variant="outline-secondary"
            className="mb-3"
            onClick={() => {
              setFiles([]);
              setErrors([]);
            }}
          >
            Clear files
          </Button>
          <Table bordered responsive size="sm">
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Print</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => (
                <tr key={index}>
                  <td>{file.name}</td>
                  <td>{(file.size / 1024).toFixed(1)} KiB</td>
                  <td>
                    <Button size="sm" aria-label={`Preview / Print ${file.name}`} onClick={() => setSelected(file)}>
                      Preview / Print
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
      {selected && <ZplPreview file={selected} onClose={() => setSelected(null)} />}
    </Container>
  );
}
